import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { Routes, Route } from "react-router-dom-v5-compat";

import BillSplitLayout from "./BillSplit/BillSplitLayout";
import { JoinBillSplitSession } from "./BillSplit/JoinBillSplitSession";
import OwnerReview from "./BillSplit/OwnerReview";
import ReviewBillSplitSesson from "./BillSplit/ReviewBillSplitSession";
import { BillSplitSelectOrderItems } from "./BillSplit/SelectOrderItems";
import { Share } from "./BillSplit/Share";
import { StartBillSplitSession } from "./BillSplit/StartBillSplitSession";
import { brandForContext, BrandContext, useBrand } from "./BrandContext";
import { BustleLayout } from "./BustleLayout";
import { EditCartItem, NewCartItem } from "./CartItem/CartItem";
import Cart from "./Checkout/Checkout";
import CheckoutSuccess, {
  TabCheckoutSuccess,
} from "./Checkout/CheckoutSuccess";
import ClearCart from "./Checkout/ClearCart";
import CheckoutLayout from "./Checkout/Layout";
import OrderPlaced from "./Checkout/OrderPlaced";
import { CustomerForm } from "./Customer/CustomerForm";
import { BillReview } from "./CustomerTab/BillReview";
import { VenueErrorPage } from "./ErrorPage";
import Loading, { useLoadingState } from "./Loading";
import MainPage from "./MainPage";
import Promotions, { Promotion } from "./Promotions";
import Section from "./Sections/Section";
import { SectionLayout } from "./Sections/SectionLayout";
import { useSession } from "./Session/SessionContext";
import { getIsWishlist } from "./Session/sessionSelectors";
import { ThankYou } from "./ThankYou";
import { VenueContext, useVenue } from "./VenueContext";
import { VenueLayout } from "./VenueLayout";
import { getMenu } from "./requests";
import type { Menu, Venue } from "./schema";

interface MobileOrderingMenuProps {
  venue: Venue;
  menu: Menu;
}

const MobileMenuProviders = ({
  venue,
  children,
}: {
  venue: Venue;
  children: React.ReactNode;
}) => {
  const brand = brandForContext(venue.venueBrand);
  const { t } = useTranslation();
  return (
    <VenueContext.Provider value={venue}>
      <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
    </VenueContext.Provider>
  );
};
export const MobileOrderingMenu = ({ menu }: MobileOrderingMenuProps) => {
  const isWishlist = getIsWishlist(useSession(), useVenue());
  const { t } = useTranslation();
  return (
    <Routes>
      <Route element={<BustleLayout />}>
        <Route path="thank-you" element={<ThankYou />} />
      </Route>
      <Route element={<VenueLayout />}>
        <Route path="billSplit" element={<BillSplitLayout />}>
          <Route path="customer" element={<CustomerForm />} />
          <Route path="join" element={<JoinBillSplitSession />} />
          <Route path="start" element={<StartBillSplitSession />} />
          <Route path="review" element={<ReviewBillSplitSesson />} />
          <Route path="ownerReview" element={<OwnerReview />} />
          <Route path="bill" element={<BillReview />} />
          <Route path="share" element={<Share />} />
          <Route index element={<BillSplitSelectOrderItems />} />
        </Route>
        {/*
          Because the root page is subtley different in how
          it handle responsiveness ( It doesn't hide some content )
          it needs to be rendered separately
        */}
        <Route path="/" element={<MainPage isRootPage menu={menu} />}>
          <Route index element={<Promotions menu={menu} />} />
        </Route>
        <Route
          path="cart/checkout/:checkoutId"
          element={<CheckoutLayout title={t("orderConfirmed.title")} />}
        >
          <Route path="success" element={<CheckoutSuccess />} />
        </Route>
        <Route
          path="tab/checkout/:checkoutId"
          element={<CheckoutLayout title={t("paymentConfirmed.title")} />}
        >
          <Route path="success" element={<TabCheckoutSuccess />} />
        </Route>
        <Route element={<MainPage menu={menu} />}>
          <Route
            path="order-items/:itemUuid/:cartItemId"
            element={<EditCartItem menu={menu} />}
          />
          <Route element={<Promotion menu={menu} />}>
            <Route
              path="promotions/:itemUuid"
              element={<NewCartItem menu={menu} />}
            />
          </Route>
          <Route path=":sectionUuid" element={<SectionLayout menu={menu} />}>
            <Route index element={<Section menu={menu} />} />
            <Route path=":itemUuid/new" element={<NewCartItem menu={menu} />} />
          </Route>
        </Route>
        <Route
          path="order"
          element={<CheckoutLayout title={t("orderConfirmed.title")} />}
        >
          <Route path=":orderId/ordered" element={<OrderPlaced />} />
        </Route>
        <Route path="clear" element={<ClearCart />} />
        <Route path="thank-you" element={<ThankYou />} />
        <Route
          path="your-order"
          element={
            <CheckoutLayout
              title={isWishlist ? t("cart.wishlistTitle") : t("cart.title")}
            />
          }
        >
          <Route path="pending" element={<Cart />} />
          <Route path="*" element={<div>Your order path not found</div>} />
        </Route>
      </Route>
    </Routes>
  );
};

const MobileOrderingMenuContainer = ({
  menuUuid,
  venue,
}: {
  venue: Venue;
  menuUuid: string;
}) => {
  const [menu, setMenu] = useState<Menu | null>();

  const loadingState = useLoadingState(async () => {
    const menu = await getMenu(menuUuid);
    setMenu(menu);
  }, []);
  return (
    <Loading loadingState={loadingState}>
      {menu && (
        <MobileMenuProviders venue={venue}>
          <MobileOrderingMenu menu={menu} venue={venue} />
        </MobileMenuProviders>
      )}
    </Loading>
  );
};
export default MobileOrderingMenuContainer;
